import { useMsal } from '@azure/msal-react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import FolderIcon from '@mui/icons-material/Folder';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import SendIcon from '@mui/icons-material/Send';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import TranslateIcon from '@mui/icons-material/Translate';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useHasFullAccess, useIsAdministrator } from '../auth/useCallerContext';
import { useSignedInUser } from '../auth/useSignedInUser';
import { useNoTenantMessageKey, useSelectedTenant } from '../tenant/tenantSelection';
import { formatDeployedVersion } from '../utils/format';
import { ColorModeToggle } from './ColorModeToggle';
import { ErrorBoundary } from './ErrorBoundary';
import { LanguageMenu } from './LanguageMenu';
import { Logo } from './Logo';
import { TenantMenu } from './TenantMenu';

const drawerWidth = 232;

/**
 * The deployed version, stamped into config.js by the API that serves the console. The vite dev
 * server serves the empty fallback instead, so local development reads as 'dev' rather than
 * pretending to be a deployment.
 */
const deployedVersion = window.__ALGOBRIDGE_CONFIG__?.version;

interface NavItem {
  to: string;
  labelKey:
    | 'nav.files'
    | 'nav.operations'
    | 'nav.communication'
    | 'nav.submit'
    | 'nav.flowis'
    | 'nav.peppol'
    | 'nav.mappings'
    | 'nav.administration'
    | 'nav.codelists';
  descriptionKey:
    | 'nav.filesDescription'
    | 'nav.operationsDescription'
    | 'nav.communicationDescription'
    | 'nav.submitDescription'
    | 'nav.flowisDescription'
    | 'nav.peppolDescription'
    | 'nav.mappingsDescription'
    | 'nav.administrationDescription'
    | 'nav.codelistsDescription';
  icon: ReactElement;
  /** Reaching it takes the Entra administrator role; without it the API answers 403. */
  administratorOnly?: boolean;
  /** Reaching it takes the `user` role; a bare `registration` submitter gets 403 from the API. */
  fullAccessOnly?: boolean;
  /** Shown only to a bare `registration` submitter — full users have the same action on Operations. */
  submitterOnly?: boolean;
  /** The section answers per tenant, so it is disabled while none is selected. */
  requiresTenant?: boolean;
  /**
   * Like requiresTenant for everybody but an administrator, who reads the section across every
   * tenant with none selected — the communication log, whose most telling rows name no tenant.
   */
  requiresTenantUnlessAdministrator?: boolean;
}

const navItems: NavItem[] = [
  {
    // The submitter's one section: hand a document in, then follow its operation detail.
    to: '/submit',
    labelKey: 'nav.submit',
    descriptionKey: 'nav.submitDescription',
    icon: <SendIcon />,
    submitterOnly: true,
    requiresTenant: true,
  },
  {
    to: '/files',
    labelKey: 'nav.files',
    descriptionKey: 'nav.filesDescription',
    icon: <FolderIcon />,
    fullAccessOnly: true,
    requiresTenant: true,
  },
  {
    to: '/operations',
    labelKey: 'nav.operations',
    descriptionKey: 'nav.operationsDescription',
    icon: <SendIcon />,
    fullAccessOnly: true,
    requiresTenant: true,
  },
  {
    to: '/communication',
    labelKey: 'nav.communication',
    descriptionKey: 'nav.communicationDescription',
    icon: <SyncAltIcon />,
    fullAccessOnly: true,
    requiresTenantUnlessAdministrator: true,
  },
  {
    to: '/mappings',
    labelKey: 'nav.mappings',
    descriptionKey: 'nav.mappingsDescription',
    icon: <TranslateIcon />,
    fullAccessOnly: true,
    requiresTenant: true,
  },
  {
    to: '/flowis',
    labelKey: 'nav.flowis',
    descriptionKey: 'nav.flowisDescription',
    icon: <CloudSyncIcon />,
    fullAccessOnly: true,
    requiresTenant: true,
  },
  {
    // The Peppol Directory check answers without a tenant, so it stays reachable with none selected.
    to: '/peppol',
    labelKey: 'nav.peppol',
    descriptionKey: 'nav.peppolDescription',
    icon: <TravelExploreIcon />,
    fullAccessOnly: true,
  },
  {
    to: '/admin',
    labelKey: 'nav.administration',
    descriptionKey: 'nav.administrationDescription',
    icon: <AdminPanelSettingsIcon />,
    administratorOnly: true,
  },
  {
    // The same right as Administration, but its own entry: the standard code lists are a shared
    // registry of their own, not a property of tenants and accounts.
    to: '/codelists',
    labelKey: 'nav.codelists',
    descriptionKey: 'nav.codelistsDescription',
    icon: <ListAltIcon />,
    administratorOnly: true,
  },
];

export function AppLayout() {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const user = useSignedInUser();
  const location = useLocation();
  const isAdministrator = useIsAdministrator();
  const hasFullAccess = useHasFullAccess();
  const { selectedTenantGid } = useSelectedTenant();
  const noTenantKey = useNoTenantMessageKey();

  // A section the API would refuse with 403 is not offered at all — a bare registration submitter
  // sees only the submit action, everybody else everything their roles reach.
  const visibleNavItems = useMemo(
    () =>
      navItems.filter(
        (item) =>
          (!item.administratorOnly || isAdministrator) &&
          (!item.fullAccessOnly || hasFullAccess) &&
          (!item.submitterOnly || !hasFullAccess),
      ),
    [isAdministrator, hasFullAccess],
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Logo height={44} />
          <Box sx={{ flexGrow: 1 }} />
          <TenantMenu />
          <Typography variant="body2" color="text.secondary">
            {user.displayName ?? user.username}
          </Typography>
          <LanguageMenu />
          <ColorModeToggle />
          <Tooltip title={t('actions.signOut')}>
            <IconButton onClick={() => void instance.logoutRedirect()} aria-label={t('actions.signOut')}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {visibleNavItems.map((item) => {
              const needsTenant =
                (item.requiresTenant ?? false) ||
                ((item.requiresTenantUnlessAdministrator ?? false) && !isAdministrator);
              const missingTenant = needsTenant && selectedTenantGid === null;
              const button = (
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  selected={location.pathname.startsWith(item.to)}
                  disabled={missingTenant}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={t(item.labelKey)} secondary={t(item.descriptionKey)} />
                </ListItemButton>
              );

              // A disabled button swallows pointer events, so the tooltip listens on a wrapper.
              return missingTenant ? (
                <Tooltip key={item.to} title={t(noTenantKey)} placement="right">
                  <Box component="span" sx={{ display: 'block' }}>
                    {button}
                  </Box>
                </Tooltip>
              ) : (
                <Fragment key={item.to}>{button}</Fragment>
              );
            })}
          </List>
          <Divider />
        </Box>
        <Box sx={{ mt: 'auto', px: 2, py: 1.5 }}>
          {deployedVersion ? (
            <Tooltip title={deployedVersion}>
              <Typography variant="caption" color="text.secondary">
                {`v${formatDeployedVersion(deployedVersion)}`}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.secondary">
              dev
            </Typography>
          )}
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
        <Toolbar />
        <ErrorBoundary key={location.key}>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
